import React from 'react';

import {format, isSameYear} from 'date-fns';
import CSSModules from 'react-css-modules';
import {useIntl} from 'react-intl';

import {formatVideoDuration} from '@edume/bento/videoUtil.browser';
import {Badge, Button} from '@edume/magnificent';

import type {Video} from '../../../state/videoLibrary/videoLibraryApi';

import styles from './styles.module.scss';

type Props = {
  video: Video;
  chooseVideo: (video: Pick<Video, 'id' | 'url' | 'filename'>) => void;
  onDelete: (id: number) => void;
};

const VideoLibraryPreviewComponent = ({
  video,
  chooseVideo,
  onDelete,
}: Props) => {
  const {thumbnailUrl, filename, duration, createdAt, isExternal} = video;
  const intl = useIntl();
  const getPreview = (imageUrl: string) =>
    imageUrl.replace(
      '/video/upload/',
      '/video/upload/w_480,h_480,c_limit,q_50,f_auto/'
    );

  const formattedDuration = formatVideoDuration(duration);
  const dateFormat = isSameYear(new Date(), createdAt)
    ? 'dd MMMM'
    : 'dd MMMM yyyy';

  return (
    <div
      styleName='library-preview-container'
      data-automation='video-preview-container'
    >
      <div styleName='overlay'>
        <button styleName='library-delete' onClick={() => onDelete(video.id)}>
          <img src='/resources/img/delete.svg' />
        </button>
        <Button
          type='secondary'
          onClick={() => chooseVideo(video)}
          noMarginTop
          noMarginLeft
          dataAutomation='select-video-button'
        >
          {intl.formatMessage({id: 'VideoLibrary.useVideo'})}
        </Button>
      </div>
      <img
        styleName='library-preview'
        src={getPreview(thumbnailUrl)}
        alt={filename}
      />
      <div styleName='library-filename-container'>
        <p styleName='library-filename' data-automation='video-filename'>
          {filename}
        </p>
        <p styleName='library-file-metadata'>
          {formattedDuration} • {format(createdAt, dateFormat)}
          {isExternal && (
            <span styleName='library-file-badge'>
              <Badge
                textContent={intl.formatMessage({
                  id: 'VideoLibrary.badge.external',
                })}
                colour='green'
                compact
              />
            </span>
          )}
        </p>
      </div>
    </div>
  );
};

export const VideoLibraryPreview = CSSModules(
  VideoLibraryPreviewComponent,
  styles
);
